// Staff records with licence expiry (docs/plans/phase2-staff-records.md §4): which licence
// notice is due on a day, the order of the expiry list, and the employment details' rules.
// Pure: the caller passes the instant. Built on `licenceStatus`, so the list, the tags, the
// clinician's own strip and the daily job always agree on a licence's state.

import { err, ok, type Result } from '../result.ts';
import { compareDays, parseIsoDay } from '../time.ts';
import { licenceStatus, type LicenceState } from './licence.ts';

/** How someone is employed (provisional list, plan S4). */
export const EMPLOYMENT_TYPES = [
  'permanent',
  'contract',
  'locum',
  'intern',
  'volunteer',
  'other',
] as const;
export type EmploymentType = (typeof EMPLOYMENT_TYPES)[number];

export const EMPLOYMENT_TYPE_LABELS: Record<EmploymentType, string> = {
  permanent: 'Permanent',
  contract: 'Contract',
  locum: 'Locum',
  intern: 'Intern',
  volunteer: 'Volunteer',
  other: 'Other',
};

export const MAX_DEPARTMENT_LENGTH = 80;

/** The notices the daily job records (plan §2.4). */
export const LICENCE_NOTICE_KINDS = ['expiring', 'expired'] as const;
export type LicenceNoticeKind = (typeof LICENCE_NOTICE_KINDS)[number];

/**
 * The notice due for a licence on the day of `at` (EAT): `expired` from the day after the
 * last valid day, `expiring` inside the warning window (the last valid day included), and
 * null otherwise or when no readable expiry is recorded.
 */
export function licenceExpiryNoticeDue(input: {
  expiresOn: string | null;
  at: Date;
  warningDays: number;
}): LicenceNoticeKind | null {
  const status = licenceStatus({ hasProfile: true, ...input });
  if (status.state === 'expired') return 'expired';
  if (status.state === 'expiring') return 'expiring';
  return null;
}

/** Whether a licence belongs on the "Licences to renew" list and the clinician's strip. */
export function needsLicenceAttention(state: LicenceState): boolean {
  return state === 'expired' || state === 'expiring';
}

/** Soonest expiry first; no expiry date last. */
export function compareLicenceExpiry(
  a: { expiresOn: string | null },
  b: { expiresOn: string | null },
): number {
  if (a.expiresOn === b.expiresOn) return 0;
  if (a.expiresOn === null) return 1;
  if (b.expiresOn === null) return -1;
  return a.expiresOn < b.expiresOn ? -1 : 1;
}

export type StaffErrorCode = 'staff.licence_issued_after_expiry' | 'staff.department_too_long';

export const STAFF_ERROR_MESSAGES: Record<StaffErrorCode, string> = {
  'staff.licence_issued_after_expiry':
    'The issue date is after the expiry date. Check both dates on the licence.',
  'staff.department_too_long': `Keep the department to ${String(MAX_DEPARTMENT_LENGTH)} characters or fewer.`,
};

/** An issue date may not fall after the expiry date; either may be missing. */
export function checkLicenceDates(input: {
  issuedOn: string | null;
  expiresOn: string | null;
}): Result<null, StaffErrorCode> {
  if (input.issuedOn === null || input.expiresOn === null) return ok(null);
  const issued = parseIsoDay(input.issuedOn);
  const expires = parseIsoDay(input.expiresOn);
  if (!issued.ok || !expires.ok) return ok(null);
  return compareDays(issued.value, expires.value) > 0
    ? err('staff.licence_issued_after_expiry')
    : ok(null);
}

/** Department is short free text until departments exist as master data (plan S3). */
export function checkDepartment(text: string | null): Result<string | null, StaffErrorCode> {
  const trimmed = text?.trim() ?? '';
  if (trimmed === '') return ok(null);
  if (trimmed.length > MAX_DEPARTMENT_LENGTH) return err('staff.department_too_long');
  return ok(trimmed);
}
